import React from "react";

import SBCLinkCell from "ComparePlans/Components/SBCLinkCell";
import { useUpdateIndividualApplicationMutation } from "IndividualApplication/api";
import { IndividualApplication, IndividualDentalPlan } from "IndividualApplication/types";
import { individualVisionPlanURL, enterInformationURL } from "IndividualApplication/urls";
import { useHistory } from "react-router-dom";
import formatCurrency from "Utils/formatCurrency";

import { individualURLHelper, individualEligibleVision, AnalyticsEvent } from "../utils";
import { Body1, SubBody } from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { CarrierLogo } from "Cobalt/CarrierLogo";
import { Subtitle } from "Cobalt/Header";
import { Surface, SurfaceSection } from "Cobalt/Surface";
import { Col, Row } from "Common/Grid";
import { Div } from "Common/HTML";

import type { StyleProps } from "Utils/styleProps";

import makeBem from "Utils/makeBem";

const bem = makeBem("IndividualPlanCard");

const detailRow = (name: string, value: string) => (
  <Div key={name} className={bem("dentalDetailRow")} mb={3}>
    <SubBody className={bem("dentalDetailRow", "detailName")} weight="bold">
      {name}:
    </SubBody>
    <SubBody className={bem("dentalDetailRow", "detailValue")}>{value}</SubBody>
  </Div>
);

const formatTerm = (term: string) => {
  if (!term) {
    return "";
  }
  const idx = term.indexOf("<br>") > 0 ? term.indexOf("<br>") : term.indexOf("<br/>");
  return idx > 0 ? term.slice(0, idx) : term;
};

export interface IndividualDentalPlanCardProps extends StyleProps {
  plan: IndividualDentalPlan;
  individualApplication: IndividualApplication;
}

export const IndividualDentalPlanCard = ({ plan, individualApplication }: IndividualDentalPlanCardProps) => {
  const history = useHistory();
  const [updateIndividualApplication] = useUpdateIndividualApplicationMutation();

  const detailsColumnOne = {
    "Max Benefit": plan.annual_maximum == "Unlimited" ? "Unlimited" : formatCurrency(parseInt(plan.annual_maximum)),
    Cleanings: plan.fine_print.Preventive["Tooth Cleaning (every 6 months)"],
    Cavities: plan.cavities,
    Braces: plan.fine_print.Orthodontics["Children (until 18)"],
  };

  const detailsColumnTwo = {
    Crowns: formatTerm(plan.fine_print.Crowns["Porcelain and Steel"]),
    "Root Canals": formatTerm(plan.fine_print.Endodontics["Root Canal (single)"]),
    Extraction: formatTerm(plan.fine_print["Tooth Extraction"].Normal),
    "Gum Cleaning": formatTerm(plan.fine_print.Periodontics["Deep Cleaning (Scaling & Planing, per quadrant)"]),
  };

  const onSubmit = async (plan) => {
    const submitValues: IndividualApplication = {
      ...individualApplication,
      individualDentalPlanID: plan.id,
    };

    await updateIndividualApplication(submitValues);

    AnalyticsEvent(`${plan.type}_selected`);
    history.push(individualURLHelper(individualEligibleVision(individualApplication) ? individualVisionPlanURL : enterInformationURL));
  };

  return (
    <Surface>
      <SurfaceSection>
        <Row>
          <Col xs={3}>
            <CarrierLogo mt={3} plan={plan} />
          </Col>
          <Col xs={7}>
            <Subtitle weight="bold">{plan.display_name}</Subtitle>
            <SubBody>{plan.deductible} Deductible</SubBody>
          </Col>
          <Col xs={2} className={bem()}>
            <SubBody mb={2}>Your Cost</SubBody>
            <Body1 weight="bold" className={bem("pricingLabel")}>
              {formatCurrency(plan.total_employee_premium)}
            </Body1>
            <Body1 className={bem("pricingLabel")}>/mo</Body1>
          </Col>
        </Row>
      </SurfaceSection>
      <SurfaceSection>
        <Row>
          <Col xs={3}>{Object.entries(detailsColumnOne).map(([name, value]) => detailRow(name, value))}</Col>
          <Col xs={3}>{Object.entries(detailsColumnTwo).map(([name, value]) => detailRow(name, value))}</Col>
          <Col xs={1} />
          <Col xs={5}>
            <Div className={bem("SBCRow")} mt={5}>
              <SubBody className={bem("SBCRow", "label")} weight="bold">
                More Details
              </SubBody>
              <SBCLinkCell plan={plan} />
            </Div>
            <Button mt={4} fullWidth onClick={() => onSubmit(plan)}>
              Enroll
            </Button>
          </Col>
        </Row>
      </SurfaceSection>
    </Surface>
  );
};
